import { SessionProvider } from 'next-auth/react';
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ExperimentProps } from 'src/services/experiments/types';
import { ExperimentProvider } from 'src/providers/ExperimentProvider';
import { GraphqlProvider } from 'src/providers/GraphqlProvider';
import { PossibleZillowSession } from 'src/services/auth/types';
import { FunctionComponentWithChildren } from 'src/utils/types';
import { ApplicationRoute } from 'src/common/constants';
import { useMigrateToZillowState } from 'src/experiences/MigrateToZillow/hooks/useMigrateToZillowState';
import { WiredZillowNavbar } from 'src/components/ZillowNavbar';
import { getHomeLoansDashboardRuntimeConfig } from 'src/services/config/getHomeLoansDashboardRuntimeConfig';
import { HEADER_VERSIONS } from 'src/components/ZillowNavbar/constants';
import { globalErrorHandler } from 'src/common/globalErrorHandler';
import { DEPLOYMENT_ENVIRONMENT } from 'env/environments';
import { DevEnvironmentWarning } from 'src/components/DevEnvironmentWarning';
import { QueryParamsManager } from 'src/services/QueryParameterManager';
import { getPublicSettingsValues } from 'src/services/settings/getPublicSettingsValues';
import { getMarkdownCMSDocument } from 'src/services/max/getMarkdownCMSDocument';
import { DatadogProvider } from 'src/services/datadog/components/DatadogProvider';
import { MaybeLoanID } from 'src/common/types';
import { UnauthorizedAccessGuardProvider } from './UnauthorizedAccessGuardProvider';
import { AnalyticsProvider } from './AnalyticsProvider';

const ZillowZHLFooter = dynamic(
    async () => {
        const { ZillowZHLFooter: Component } = await import(
            /* webpackChunkName: "Footer" */
            '@zillow/zhl-page-structure'
        );
        return Component;
    },
    // This footer causes SSR hydration issues
    { ssr: false }
);

export const UniversalWrappers: FunctionComponentWithChildren<
    {
        session: PossibleZillowSession;
        loanID: MaybeLoanID;
    } & ExperimentProps
> = ({ session, loanID, children, ...rest }) => {
    useMigrateToZillowState();
    useEffect(() => {
        window.addEventListener('error', globalErrorHandler);
        return () => {
            window.removeEventListener('error', globalErrorHandler);
        };
    });
    const { publicRuntimeConfig } = getHomeLoansDashboardRuntimeConfig();
    const { ENV } = publicRuntimeConfig;
    return (
        // check both because origin is server side, but currentOrigin is client side
        <GraphqlProvider>
            <SessionProvider basePath={`${ApplicationRoute.API_BASE}/auth`} refetchOnWindowFocus session={session}>
                <ExperimentProvider {...rest}>
                    <AnalyticsProvider>
                        <WiredZillowNavbar env={ENV} version={HEADER_VERSIONS.ZHL_DEFAULT} />
                        <UnauthorizedAccessGuardProvider>
                            {ENV === DEPLOYMENT_ENVIRONMENT.DEVELOPMENT && <DevEnvironmentWarning />}
                            <DatadogProvider loanID={loanID}>{children}</DatadogProvider>
                        </UnauthorizedAccessGuardProvider>
                        <ZillowZHLFooter
                            queryParameters={QueryParamsManager.store}
                            onGeneralCallZHL={() => {}} //what event to send for this?
                            onError={() => {}}
                            getPublicSettingsValues={getPublicSettingsValues}
                            getMarkdownCMSDocument={getMarkdownCMSDocument}></ZillowZHLFooter>
                    </AnalyticsProvider>
                </ExperimentProvider>
            </SessionProvider>
        </GraphqlProvider>
    );
};
